import React from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, ArrowRight, CheckCircle2, FileText, LayoutTemplate } from 'lucide-react';
import { btnPrimary, btnSecondary } from './DocIntelUi';

const DocIntelApplyPanel = ({ dealId, meta, applyResult, onOpenTemplateStudio }) => {
  const navigate = useNavigate();
  const skipped = applyResult?.skipped ?? [];
  const hasStats =
    applyResult &&
    applyResult.application_updates != null &&
    applyResult.field_overrides != null;

  return (
    <div className="flex flex-col px-4 py-8 sm:px-8 sm:py-10">
      <div className="mx-auto w-full max-w-lg text-center">
        <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-emerald-100 ring-4 ring-emerald-50">
          <CheckCircle2 className="h-8 w-8 text-emerald-600" strokeWidth={2} />
        </div>
        <h3 className="text-xl font-semibold tracking-tight text-slate-900">
          {meta?.title || 'Applied to deal'}
        </h3>
        {meta?.subtitle && (
          <p className="mt-1 text-sm text-slate-500">{meta.subtitle}</p>
        )}
        <p className="mt-3 text-sm leading-relaxed text-slate-600">
          {hasStats
            ? 'Application updated and field overrides saved. Download a fresh agreement draft to verify name, Aadhaar, and other mapped fields.'
            : 'This document was applied earlier. Open the agreement page to download an updated draft.'}
        </p>
      </div>

      {hasStats && (
        <div className="mx-auto mt-8 grid w-full max-w-lg grid-cols-3 gap-3">
          <StatCard label="Application updates" value={applyResult.application_updates} accent="teal" />
          <StatCard label="Field overrides" value={applyResult.field_overrides} accent="emerald" />
          <StatCard
            label="Skipped"
            value={skipped.length}
            accent={skipped.length > 0 ? 'amber' : 'slate'}
          />
        </div>
      )}

      {skipped.length > 0 && (
        <div className="mx-auto mt-4 w-full max-w-lg rounded-xl border border-amber-200 bg-amber-50/90 px-4 py-3 text-left">
          <div className="mb-2 flex items-center gap-2 text-sm font-medium text-amber-900">
            <AlertTriangle className="h-4 w-4" />
            Skipped fields
          </div>
          <ul className="space-y-1 text-xs text-amber-900/90">
            {skipped.map((item) => (
              <li key={item} className="rounded-md bg-white/60 px-2 py-1">
                {item}
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="mx-auto mt-6 w-full max-w-lg rounded-xl border border-teal-200/80 bg-teal-50/50 px-4 py-3 text-left">
        <div className="flex gap-3">
          <FileText className="mt-0.5 h-5 w-5 shrink-0 text-teal-800" />
          <div>
            <p className="text-sm font-medium text-teal-950">Regenerate agreement draft</p>
            <p className="mt-1 text-xs leading-relaxed text-teal-900/85">
              The agreement PDF is rebuilt from the latest application data each time you download.
              If you previously uploaded a signed copy, that stored file is separate — use
              download on the agreement page for the new draft.
            </p>
          </div>
        </div>
      </div>

      <div className="mx-auto mt-8 w-full max-w-lg rounded-xl border border-slate-100 bg-slate-50/50 p-4">
        <p className="text-center text-xs font-medium text-slate-500">Where to next?</p>
        <div className="mt-3 flex flex-col gap-2">
          {onOpenTemplateStudio && (
            <button
              type="button"
              onClick={onOpenTemplateStudio}
              className={`${btnSecondary} w-full`}>
              <LayoutTemplate className="h-4 w-4 text-teal-700" />
              Open Template Studio
              <span className="text-xs font-normal text-slate-500">(optional)</span>
            </button>
          )}
          <button
            type="button"
            onClick={() => navigate(`/builder/Agreement/${dealId}`)}
            className={`${btnPrimary} w-full`}>
            Open agreement draft
            <ArrowRight className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

function StatCard({ label, value, accent }) {
  const accents = {
    teal: 'border-teal-100 bg-teal-50/50 text-teal-900',
    emerald: 'border-emerald-100 bg-emerald-50/50 text-emerald-900',
    amber: 'border-amber-100 bg-amber-50/50 text-amber-900',
    slate: 'border-slate-200 bg-white text-slate-900'
  };

  return (
    <div className={`rounded-xl border px-3 py-4 text-center shadow-sm ${accents[accent] || accents.slate}`}>
      <p className="text-2xl font-bold tabular-nums">{value}</p>
      <p className="mt-1 text-[11px] leading-snug text-slate-600">{label}</p>
    </div>
  );
}

export default DocIntelApplyPanel;
